var _react_refresh_temp_1, _react_refresh_temp_2;
var _react_refresh_temp_3, _react_refresh_temp_4;
_react_refresh_temp_3 = $RefreshSig$();
_react_refresh_temp_4 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useLayoutEffect, useMemo } from 'react';
import { NavigationType, useBlocker, useLocation, useNavigate, useSearchParams, } from 'react-router-dom';
import { z } from '@lucidlink/dto/z';
import { REPLACE_ON_NAVIGATE } from './Drawer.consts';
import { DrawerConfigSchema } from './DrawerConfig.schema';
import { DrawerContext } from './DrawerContext';
const DRAWER_SEARCH_PARAM = 'drawer';
const closedState = { isOpen: false, params: null };
const parseDrawerConfig = (rawConfig) => {
    try {
        return JSON.parse(rawConfig);
    }
    catch {
        return null;
    }
};
export const DrawerProvider = ({ children }) => {
    _react_refresh_temp_3();
    const [searchParams, setSearchParams] = useSearchParams();
    const state = useMemo(() => {
        const rawConfig = searchParams.get(DRAWER_SEARCH_PARAM);
        if (!rawConfig) {
            return closedState;
        }
        const parsedDrawerConfig = DrawerConfigSchema.safeParse(parseDrawerConfig(rawConfig));
        if (parsedDrawerConfig.error) {
            return closedState;
        }
        return {
            isOpen: true,
            params: parsedDrawerConfig.data,
        };
    }, [searchParams]);
    const isCurrentDrawerTransient = state.isOpen && REPLACE_ON_NAVIGATE[state.params.type];
    const closeDrawer = useCallback(() => {
        setSearchParams((prev) => {
            prev.delete(DRAWER_SEARCH_PARAM);
            return prev;
        }, { flushSync: true, replace: isCurrentDrawerTransient });
    }, [setSearchParams, isCurrentDrawerTransient]);
    const openDrawer = useCallback((params) => {
        setSearchParams((prev) => {
            prev.set(DRAWER_SEARCH_PARAM, JSON.stringify(z.encode(DrawerConfigSchema, params)));
            return prev;
        }, { flushSync: true, replace: isCurrentDrawerTransient });
    }, [setSearchParams, isCurrentDrawerTransient]);
    const value = useMemo(() => ({
        ...state,
        openDrawer,
        closeDrawer,
    }), [state, openDrawer, closeDrawer]);
    return (_jsxs(DrawerContext.Provider, { value: value, children: [isCurrentDrawerTransient && _jsx(TransientDrawerNavigationGuard, {}), children] }));
};
_react_refresh_temp_1 = DrawerProvider;
$RefreshReg$(_react_refresh_temp_1, "DrawerProvider");
_react_refresh_temp_3(DrawerProvider, "uPOI0qwUdUJDegt2I1tivC+Xb54=", false, () => [useSearchParams]);
/**
 * Re-issues foreign PUSH navigation as REPLACE while a transient drawer is
 * open, so Back skips the drawer's history entry. React Router supports a
 * single active blocker per router (last registered wins), so this guard is
 * mounted only while a transient drawer is open to avoid starving other
 * useBlocker consumers (e.g. the backup-code blocker in DesktopClient).
 */
const TransientDrawerNavigationGuard = () => {
    _react_refresh_temp_4();
    const [, setSearchParams] = useSearchParams();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    /**
     * Drawer navigation issued by openDrawer/closeDrawer already replaces
     * synchronously, so the blocker only catches foreign PUSH navigation
     * (e.g. leaving the route). POP and REPLACE pass through: our own
     * re-issued navigation is a REPLACE, and POP already leaves the
     * drawer's history entry.
     */
    const navigationBlocker = useBlocker(({ historyAction }) => historyAction === NavigationType.Push);
    /**
     * Layout effect so the re-issued navigation lands before the blocked
     * frame is painted — a plain effect shows a one-frame flash.
     */
    useLayoutEffect(() => {
        if (navigationBlocker.state !== 'blocked') {
            return;
        }
        const blockedLocation = navigationBlocker.location;
        if (blockedLocation.pathname === pathname) {
            setSearchParams(blockedLocation.search, {
                flushSync: true,
                replace: true,
            });
            return;
        }
        void navigate(blockedLocation, {
            replace: true,
            state: blockedLocation.state,
        });
    }, [navigate, navigationBlocker, pathname, setSearchParams]);
    return null;
};
_react_refresh_temp_2 = TransientDrawerNavigationGuard;
$RefreshReg$(_react_refresh_temp_2, "TransientDrawerNavigationGuard");
_react_refresh_temp_4(TransientDrawerNavigationGuard, "w1iQMh4mxtm4dZ//WzSzSZZ1MdE=", false, () => [useSearchParams, useLocation, useNavigate, useBlocker]);
